import {
  IPublicClientApplication,
  PublicClientApplication,
  LogLevel,
  InteractionType,
} from '@azure/msal-browser';
import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { DEFAULT_AUTH_SCOPES } from './auth.constants';

export class MsalConfigService {
  private static instance: IPublicClientApplication | null = null;

  public static createMsalInstance(): IPublicClientApplication {
    function isIE(): boolean {
      const ua = window.navigator.userAgent;
      const msie = ua.indexOf('MSIE ');
      const trident = ua.indexOf('Trident/');
      return msie > 0 || trident > 0;
    }

    function loggerCallback(logLevel: LogLevel, message: string, containsPii: boolean): void {
      if (!containsPii) {
        switch (logLevel) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          case LogLevel.Verbose:
            console.debug(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          default:
            console.log(message);
        }
      }
    }

    if (!MsalConfigService.instance) {
      const redirectUri = environment.azureActiveDirectory.redirectUri || window.location.origin;
      const postLogoutRedirectUri =
        environment.azureActiveDirectory.postLogoutRedirectUri || window.location.origin;
      const knownAuthority = MsalConfigService.resolveKnownAuthority();

      MsalConfigService.instance = new PublicClientApplication({
        auth: {
          clientId: environment.azureActiveDirectory.clientId,
          authority: `${environment.azureActiveDirectory.instance}${environment.azureActiveDirectory.tenant}${environment.azureActiveDirectory.policyName}`,
          knownAuthorities: [knownAuthority],
          redirectUri,
          postLogoutRedirectUri,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: isIE(),
        },
        system: {
          loggerOptions: {
            loggerCallback: loggerCallback,
            logLevel: LogLevel.Warning,
            piiLoggingEnabled: false,
          },
        },
      });
    }

    return MsalConfigService.instance;
  }

  public static msalInterceptorConfig(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>([
      [
        'https://identity.cafler.com/my-identity/Business.Services',
        ['https://identity.cafler.com/my-identity/Business.Services/read'],
      ],
      [
        'https://identity.cafler.com/my-identity/Business.Identity',
        ['https://identity.cafler.com/my-identity/Business.Identity/read'],
      ],
    ]);
    return {
      interactionType: MsalConfigService.resolveInteractionType(),
      protectedResourceMap: protectedResourceMap,
    };
  }

  public static msalGuardConfig(): MsalGuardConfiguration {
    return {
      interactionType: MsalConfigService.resolveInteractionType(),
      authRequest: {
        scopes: [...DEFAULT_AUTH_SCOPES],
      },
    };
  }

  private static resolveInteractionType(): InteractionType.Popup | InteractionType.Redirect {
    return MsalConfigService.isRunningInIframe() ? InteractionType.Popup : InteractionType.Redirect;
  }

  private static isRunningInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }

  private static resolveKnownAuthority(): string {
    const instance = environment.azureActiveDirectory.instance;
    try {
      return new URL(instance).hostname;
    } catch {
      return instance;
    }
  }
}
